'use client';

import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useAuth } from '@/context/auth-context';
import { useFirestore } from '@/firebase';
import { Subscription, compareSubscriptions } from '@/lib/subscriptions';

/**
 * The signed-in buyer's subscription to one product, live.
 *
 * The product page needs this for two things the ownedProducts entitlement
 * cannot answer on its own: whether there is a live subscription to offer a
 * Cancel action for, and (once canceled) the date access runs out. Both live
 * on the subscription document.
 *
 * At most one subscription per product can be live at a time (enforced in
 * createCheckoutPaymentIntent and again in fulfilAirwallexPayment), but a buyer
 * who canceled and resubscribed has several documents, so the result is ordered
 * with compareSubscriptions (the same ordering the dashboard uses) and the
 * first is returned: a live one if there is one, otherwise the most recently
 * canceled, which is the one whose access deadline still matters.
 *
 * The query is served by the existing (subscriberUid, productId, status)
 * composite index in firestore.indexes.json. Returns null while loading, for
 * signed-out visitors, and for products the buyer has never subscribed to.
 */
export function useProductSubscription(productId?: string): Subscription | null {
    const { authUser } = useAuth();
    const firestore = useFirestore();
    const [subscription, setSubscription] = useState<Subscription | null>(null);

    useEffect(() => {
        if (!authUser || authUser.isAnonymous || !productId) {
            setSubscription(null);
            return;
        }

        const q = query(
            collection(firestore, 'subscriptions'),
            where('subscriberUid', '==', authUser.uid),
            where('productId', '==', productId),
        );
        const unsubscribe = onSnapshot(
            q,
            snap => {
                const rows = snap.docs.map(d => ({ id: d.id, ...d.data() } as Subscription));
                rows.sort(compareSubscriptions);
                setSubscription(rows[0] ?? null);
            },
            // Fail closed: no subscription means no Cancel action shown. The
            // dashboard remains the complete view either way.
            () => setSubscription(null),
        );
        return () => unsubscribe();
    }, [authUser, firestore, productId]);

    return subscription;
}
